import React, { useState, useEffect } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import authHeader from "services/auth-header";
import API from "services/axios";
import { setNotification } from "features/Notification/notificationSlice";
import { useDispatch } from "react-redux";
import Spinner from "components/Spinner";

export default function LoadTemplate({
  showModal,
  setRowsData,
  setColumnLabels,
  setSpreadsheetData,
}) {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [templateData, setTemplateData] = useState(null);

  const [selectedWo, setSelectedWo] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState(null);

  const loadingOn = () => {
    setLoading(true);
  };

  const loadingOff = () => {
    setLoading(false);
  };

  const SearchWorkOrder = async () => {
    try {
      loadingOn();
      const { data } = await API.post(
        "/work-order/search",
        { searchText },
        { headers: authHeader() },
      );
      if (data?.success) {
        setSearchResults(data?.data);
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    } finally {
      loadingOff();
    }
  };

  const fetchTemplates = async () => {
    try {
      loadingOn();
      if (!selectedWo) {
        alert("Please select a work order first");
        return;
      }

      let fileName = `${selectedWo?.woId}-${selectedWo?.jobName}` + ".xlsx";
      let folder = `kms/wo/${selectedWo?.id}/attachments`;

      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URI}/aws/images`,
        {
          key: `${folder}/${fileName}`,
        },
      );

      if (!data || data.length === 0) {
        dispatch(
          setNotification({
            message: "No templates found for this work order",
            type: "error",
          }),
        );
      }

      setTemplateData(data[0]);
    } catch (err) {
      console.log(err.message);
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    } finally {
      loadingOff();
    }
  };

  const loadTemplate = async () => {
    try {
      setLoading(true);

      const url = templateData;

      // Fetch the file content
      const fileResponse = await fetch(url);
      const fileBlob = await fileResponse.blob();

      // Read the Excel file
      const fileReader = new FileReader();

      fileReader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });

          // Process the first sheet (Order label Sheet)
          const firstSheetName = workbook.SheetNames[0];
          const worksheet1 = workbook.Sheets[firstSheetName];
          const sheetData = XLSX.utils.sheet_to_json(worksheet1, { header: 1 });

          if (sheetData.length < 2) {
            throw new Error("Invalid template file format");
          }

          // Extract column labels from the first row
          const extractedColumnLabels = sheetData[0];

          // Extract spreadsheet data from remaining rows
          const extractedSpreadsheetData = sheetData
            .slice(1)
            .map((row) => row.map((value) => ({ value, readOnly: true })));

          // Process the second sheet (Template Config)
          const secondSheetName = workbook.SheetNames[1];
          const worksheet2 = workbook.Sheets[secondSheetName];
          const configData = XLSX.utils.sheet_to_json(worksheet2);

          // Convert config data to rowsData format
          const extractedRowsData = configData.map((row) => {
            const rowData = {
              rowNo: Number(row.rowNo),
              rowType: row.rowType,
              rowValues: {},
            };

            // Add customHeader if exists
            if (row.customHeader) {
              rowData.rowValues.customHeader = row.customHeader;
            }

            // Add hideHeader if exists
            if (row.hideHeader !== undefined && row.hideHeader !== "") {
              rowData.rowValues.hideHeader =
                row.hideHeader === "true" || row.hideHeader === true;
            }

            // Add column if exists
            if (row.column) {
              rowData.rowValues.column = row.column;
            }

            // Add columns if exists
            if (row.columns) {
              rowData.rowValues.columns = row.columns.split(", ");
            }

            return rowData;
          });

          // Update the state with the extracted data
          setColumnLabels(extractedColumnLabels);
          setSpreadsheetData(extractedSpreadsheetData);
          setRowsData(extractedRowsData);

          dispatch(
            setNotification({
              message: "Template loaded successfully",
              type: "success",
            }),
          );
          showModal(false);
          setLoading(false);
        } catch (error) {
          console.error("Error parsing Excel file:", error);
          alert("Failed to parse the template file");
          setLoading(false);
        }
      };

      fileReader.onerror = () => {
        console.error("Error reading file");
        alert("Error reading the template file");
        setLoading(false);
      };

      fileReader.readAsArrayBuffer(fileBlob);
    } catch (error) {
      console.error("Error loading template:", error);
      alert("An error occurred while loading the template");
      setLoading(false);
    }
  };

  useEffect(() => {
    let timeout = setTimeout(() => {
      if (searchText) {
        SearchWorkOrder();
      } else {
        setSearchResults(null);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchText]);

  useEffect(() => {
    let timeout = setTimeout(() => {
      if (selectedWo) {
        fetchTemplates();
      }
    }, 50);
    return () => clearTimeout(timeout);
  }, [selectedWo]);

  return (
    <div className="p-4 bg-white rounded shadow">
      <h3 className="text-xl font-bold mb-4">Load Template</h3>
      {loading && <Spinner />}

      {/* Search Work Order */}
      <div className="mb-4">
        <label
          htmlFor="searchWorkOrder"
          className="block text-sm font-medium mb-1"
        >
          Search Work Order
        </label>
        <input
          type="text"
          id="searchWorkOrder"
          className="form-control"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Search job name or work order ID or customer name"
        />

        {searchResults && searchResults.length > 0 && (
          <div
            className="mt-2 bg-light py-2 px-2 rounded shadow-sm"
            style={{ maxHeight: "200px", overflowY: "auto" }}
          >
            {searchResults.map((item) => (
              <div
                key={item.id}
                className="bg-white shadow-sm custom-border-radius-sm p-1 px-2 mb-1 d-flex align-items-center justify-content-between"
              >
                <div className="w-25">{item.woId}</div>
                <div className="w-50">
                  {item?.jobName?.length > 15
                    ? `${item.jobName?.slice(0, 15)}...`
                    : item.jobName}
                </div>
                <div className="w-50">
                  {item?.customerName?.length > 13
                    ? `${item.customerName?.slice(0, 13)}...`
                    : item.customerName}
                </div>
                <button
                  onClick={() => {
                    setSelectedWo(item);
                    setSearchText(item.name);
                    setSearchResults(null);
                  }}
                  className="btn btn-sm p-0 px-1 btn-primary"
                >
                  <i className="mdi mdi-check"></i>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="d-flex justify-content-end gap-1">
        <button
          onClick={loadTemplate}
          className="btn btn-sm btn-primary"
          disabled={!selectedWo || !templateData || loading}
        >
          {loading ? "Loading..." : "Load Template"}
        </button>
        <button
          onClick={() => showModal(false)}
          className="btn btn-sm btn-secondary"
          disabled={loading}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
